// web/src/features/team/player/RosterPlayerContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { App } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { t } from '@sportninja/common/i18n'
import {
  initialRosterPlayerValues,
  toRosterCreatePayload,
  toRosterCopyPayload,
  toRosterUpdatePayload,
  mapRosterPlayerToForm,
  mapSearchPlayerToForm,
  isRosterEmailLocked,
  isRosterNamesLocked,
  isRosterSalesforceIdLocked,
  ROSTER_BE_TO_FORM_FIELD,
  ROSTER_BE_TO_FIELD_TITLE,
  PLAYER_SUCCESS_MESSAGES,
  type RosterMode,
  type RosterPlayerFormValues,
  type RosterPlayerListItem,
  type RosterSearchPlayer,
} from '@sportninja/common/features/team/player'
import {
  applyPlayerApiError,
  toSavedFormBaseline,
} from '@sportninja/common/features/profile/player'
import { formatPlayerName } from '@sportninja/common/features/profile/player/playerDetails/utils/formatPlayerName'
import {
  mapProfileExtrasToForm,
  toProfileExtrasPayload,
} from '@sportninja/common/features/profile/player/playerDetails/utils/playerProfileExtras'
import {
  ADMIN_BE_TO_FIELD_TITLE,
  ADMIN_BE_TO_FORM_FIELD,
  initialPlayerDetailsAdminValues,
} from '@sportninja/common/features/profile/player/playerDetails/constants'
import type { PlayerDetailsAdminValues } from '@sportninja/common/features/profile/player/playerDetails/types'
import {
  canManageFamily,
  toInviteStatus,
} from '@sportninja/common/features/competition/operations/utils/rosterRows'
import { EditPlayerContext } from 'src/features/profile/player/setupModal/EditPlayerContext'
import {
  SectionEditContext,
  type SectionEditContextValue,
} from 'src/features/profile/player/setupModal/context/SectionEditContext'
import { scrollToFirstFieldError } from 'src/helpers/scrollToFirstFieldError'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import {
  useCreateRosterPlayer,
  useCopyRosterPlayer,
  useUpdateRosterPlayer,
  useRosterPlayerImageMutation,
  useRosterPlayerDetail,
  useDefaultPositionId,
} from './hooks/useRosterPlayer'

interface RosterPlayerContextValue {
  isLoading: boolean
  /**
   * Edit mode: "First Last" of the fetched player ('' until it lands), for the
   * Player Details header.
   */
  playerName: string
  /**
   * Edit mode only: the single-player GET that seeds the form is still in
   * flight. The modal shows its skeleton until this clears — the form is never
   * rendered against half-seeded values.
   */
  isSeeding: boolean
  /**
   * Close the editor. In edit mode, after at least one section was saved, this
   * runs the caller's `onSuccess` instead (its list refresh and close), since
   * a section save keeps the editor open.
   */
  onCancel: () => void
  sportId: string | null
  hasAdminPermission: boolean
  /** Roster requires email → email field is mandatory + shows required. */
  requireEmail: boolean
  /** True when editing an existing roster player (vs adding). Drives the title. */
  isEdit: boolean
  /** Add-existing (copy) mode → personal fields are shown read-only. */
  isAddExisting: boolean
  /**
   * Email is locked when it's the player's settled identity: add-existing
   * (always read-only) or an edited player who has accepted their invite
   * (is_accepted). An invited-but-unaccepted player keeps email editable.
   */
  emailLocked: boolean
  /**
   * First/Last Name are locked only in add-existing (copy) mode. Editing an own
   * roster player — even an accepted-invite one — keeps names editable.
   */
  namesLocked: boolean
  /**
   * The Salesforce ID (Canlan only) can be set on create and corrected on edit.
   * Read-only for add-existing, where the record belongs to another team.
   */
  salesforceIdLocked: boolean
  /**
   * Edit mode only: show the Family Members section. Needs the modal to opt in
   * (`familyMembers`), an admin, and a player who has accepted their invite.
   */
  showFamilySection: boolean
  teamId: string
  rosterId: string
  /** The seeded roster player the Family Members section is scoped to. */
  familyPlayer: RosterPlayerListItem | undefined
}

/**
 * The roster form: the roster player's fields plus the extra Player Details
 * sections it shows on edit and add-new (Previous Experience —
 * PROFILE_EXTRA_SECTIONS).
 */
type RosterPlayerFormWithExtras = RosterPlayerFormValues &
  PlayerDetailsAdminValues

const RosterPlayerContext = createContext<RosterPlayerContextValue | null>(null)

export const useRosterPlayerContext = (): RosterPlayerContextValue => {
  const ctx = useContext(RosterPlayerContext)
  if (!ctx) {
    throw new Error(
      'useRosterPlayerContext must be used within a RosterPlayerProvider'
    )
  }
  return ctx
}

interface RosterPlayerProviderProps {
  teamId: string
  rosterId: string
  sportId: string | null
  sportName?: string
  hasAdminPermission?: boolean
  /** Roster's require_email flag → makes email mandatory + shows required. */
  requireEmail?: boolean
  /** Show the Family Members section when editing (default false). */
  familyMembers?: boolean
  /**
   * Add-existing mode: the copy target id. Personal fields come from
   * `searchPlayer` (no profile fetch). Ignored when `editPlayer` is provided.
   */
  playerId?: string
  /**
   * Add-existing mode: the picked search row (name/email/image). The form is
   * seeded from this and personal fields are shown read-only; Save copies only
   * the roster fields (the BE keeps the player's own profile).
   */
  searchPlayer?: RosterSearchPlayer
  /**
   * Edit mode: the existing roster player row (from the roster list response).
   * Only its `id` selects the player — the form itself is seeded from a fresh
   * GET .../players/:id (see `useRosterPlayerDetail`), so the list row may be
   * stale without affecting the edit. Takes precedence over `playerId`.
   */
  editPlayer?: RosterPlayerListItem
  onClose: () => void
  onSuccess?: () => void
  children: React.ReactNode
}

export const RosterPlayerProvider: React.FC<RosterPlayerProviderProps> = ({
  teamId,
  rosterId,
  sportId,
  hasAdminPermission = false,
  requireEmail = false,
  familyMembers = false,
  playerId,
  searchPlayer,
  editPlayer,
  onClose,
  onSuccess,
  children,
}) => {
  const { message } = App.useApp()
  const { create, isPending: isCreating } = useCreateRosterPlayer()
  const { copy, isPending: isCopying } = useCopyRosterPlayer()
  const { update, isPending: isUpdating } = useUpdateRosterPlayer()
  const {
    uploadImage,
    deleteImage,
    isPending: isImagePending,
  } = useRosterPlayerImageMutation()
  const defaultPositionId = useDefaultPositionId(sportId)

  const isEdit = Boolean(editPlayer)
  // Edit mode reads first (Player Details' pattern): one section is edited at
  // a time, and a save returns it to read mode with the editor still open.
  const [editingSection, setEditingSection] = useState<string | null>(null)
  // Whether a section save landed since the editor opened, so closing it hands
  // over to the caller's onSuccess (list refresh) rather than a plain close.
  const [hasSavedSection, setHasSavedSection] = useState(false)
  // The player id we save against: edit → the row's id; add-existing → playerId.
  const targetPlayerId = editPlayer?.id ?? playerId

  const isAddExisting = !isEdit && Boolean(searchPlayer ?? playerId)

  // Edit mode seeds from a fresh single-player GET rather than the list row, so
  // the form always opens on current data. The row supplies only the id.
  const { player: fetchedPlayer, error: fetchPlayerError } =
    useRosterPlayerDetail(teamId, rosterId, editPlayer?.id, isEdit)

  // The authoritative edit-mode player: the fetched record. Until it arrives the
  // form is not rendered (the modal shows its skeleton), so seeding never falls
  // back to the possibly-stale row.
  const editSource: RosterPlayerListItem | undefined = isEdit
    ? fetchedPlayer ?? undefined
    : undefined

  const mode: RosterMode = isEdit
    ? 'edit'
    : isAddExisting
    ? 'addExisting'
    : 'create'

  // Lock email only when it's the player's settled identity (add-existing, or an
  // edited player who accepted their invite). Shared rule with mobile. Reads the
  // FETCHED player so an invite accepted since the list loaded is respected.
  const emailLocked = isRosterEmailLocked({ mode, editPlayer: editSource })

  // Lock names only in add-existing (copy) — editing (even accepted) keeps them
  // editable so a typo can be fixed. Deliberately separate from emailLocked.
  const namesLocked = isRosterNamesLocked(mode)

  // Editable on create + edit (admins can correct it); locked only in
  // add-existing, where the record belongs to another team.
  const salesforceIdLocked = isRosterSalesforceIdLocked(mode)

  // Family needs the seeded player (its invite status), so it stays hidden until
  // the fetch lands; the modal shows its skeleton until then anyway.
  const showFamilySection =
    familyMembers &&
    isEdit &&
    Boolean(editSource) &&
    canManageFamily(toInviteStatus(editSource ?? {}), hasAdminPermission)

  // Formik key: remount on mode/target so opening a different player (or
  // switching between create/add-existing/edit) always re-seeds initial values.
  // Includes defaultPositionId so a late types load still seeds the dropdown,
  // and (edit) whether the fetch has landed, so the form re-seeds from the
  // fetched player instead of keeping the pre-fetch blank values.
  const formikKey = `ready:${
    isEdit
      ? `edit:${targetPlayerId}:${editSource ? 'loaded' : 'pending'}`
      : targetPlayerId ?? 'create'
  }:${defaultPositionId}`

  // Initial values:
  // - Edit → prefill personal AND roster fields from the FETCHED player.
  // - Add-existing → seed name/email/image from the picked search row (no
  //   fetch); personal shown read-only, roster fields blank.
  // - Create → fully blank.
  const seededValues: RosterPlayerFormValues = editSource
    ? mapRosterPlayerToForm(editSource)
    : searchPlayer
    ? mapSearchPlayerToForm(searchPlayer)
    : initialRosterPlayerValues

  // Pre-select the sport's default position whenever the form has none (add-new
  // and add-existing; edit carries the player's own). Without it Position starts
  // blank and a save sends player_type_id: null.
  const rosterInitialValues: RosterPlayerFormValues =
    seededValues.positionId === '' && defaultPositionId !== ''
      ? { ...seededValues, positionId: defaultPositionId }
      : seededValues
  // Edit reads the extra sections off the fetched player; add-new starts them
  // blank. Add-existing never shows them (it copies roster fields only).
  const initialValues: RosterPlayerFormWithExtras = {
    ...rosterInitialValues,
    ...initialPlayerDetailsAdminValues,
    ...(editSource ? mapProfileExtrasToForm(editSource) : {}),
  }

  const isLoading = isCreating || isCopying || isUpdating || isImagePending
  // Edit mode: seeding is incomplete until the fetch lands (or fails).
  const isSeeding = isEdit && !editSource && !fetchPlayerError

  // Keep the latest onClose in a ref so the error effect doesn't re-fire (and
  // double-toast / double-close) if a parent passes a new onClose identity.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Seed fetch failed → toast and close, matching the profile Edit Player modal.
  useEffect(() => {
    if (fetchPlayerError) {
      message.error('Could not load player. Please try again.')
      onCloseRef.current()
    }
  }, [fetchPlayerError, message])

  const handleSubmit = async (
    values: RosterPlayerFormWithExtras,
    helpers: FormikHelpers<RosterPlayerFormWithExtras>
  ) => {
    // Edit → diff PUT; add-existing copy → roster fields only (BE keeps the
    // player's profile); create-new → full new-player body.
    const body = isEdit
      ? {
          ...toRosterUpdatePayload(values, initialValues),
          ...toProfileExtrasPayload(values, initialValues),
        }
      : playerId
      ? toRosterCopyPayload(values)
      : {
          ...toRosterCreatePayload(values),
          // Only what was filled in (diffed against the blank form).
          ...toProfileExtrasPayload(values, initialValues),
        }
    try {
      // Edit → PUT update (existing row id). Add-existing → copy (searched id).
      // Create-new → POST create. All return the roster player record.
      const saved = editPlayer
        ? await update({
            teamId,
            rosterId,
            playerId: editPlayer.id,
            body,
          })
        : playerId
        ? await copy({ teamId, rosterId, playerId, body })
        : await create({ teamId, rosterId, body })

      // Image step AFTER save (needs the player id). Edit → row id; add-existing
      // → searched id; create → the new record's id. A failure leaves the player
      // on the roster — surface a toast, don't abort.
      const imagePlayerId = targetPlayerId ?? saved?.id
      if (values.imageData && imagePlayerId) {
        try {
          await uploadImage({
            teamId,
            rosterId,
            playerId: imagePlayerId,
            file: values.imageData,
          })
        } catch {
          message.error(`${t('errors:imageUploadProblem', {})}.`)
        }
      } else if (isEdit && values.imageDeleted && imagePlayerId) {
        // Edit only: the user removed the existing avatar.
        try {
          await deleteImage({ teamId, rosterId, playerId: imagePlayerId })
        } catch {
          message.error(`${t('errors:imageUploadProblem', {})}.`)
        }
      }

      message.success(
        formatSuccessToast(
          isEdit
            ? PLAYER_SUCCESS_MESSAGES.rosterEdit
            : PLAYER_SUCCESS_MESSAGES.rosterAdd
        )
      )
      if (isEdit) {
        // Back to read mode, with the staged image committed so the next
        // section's save never re-sends it; the single-player refetch then
        // re-seeds the form (setValues, not resetForm — see EditPlayerContext).
        // The caller's refresh runs on close.
        helpers.setValues(toSavedFormBaseline(values), false)
        setHasSavedSection(true)
        setEditingSection(null)
        return
      }
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      applyPlayerApiError(err, {
        fieldMap: { ...ROSTER_BE_TO_FORM_FIELD, ...ADMIN_BE_TO_FORM_FIELD },
        titleMap: { ...ROSTER_BE_TO_FIELD_TITLE, ...ADMIN_BE_TO_FIELD_TITLE },
        setErrors: helpers.setErrors,
        setTouched: (touched) => helpers.setTouched(touched, false),
        notifyError: message.error,
        // Bring the first errored field into view + focus it.
        onFieldErrors: () => scrollToFirstFieldError(),
      })
    } finally {
      helpers.setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isEdit && hasSavedSection && onSuccess) {
      onSuccess()
      return
    }
    onClose()
  }

  const sectionEdit: SectionEditContextValue = {
    editingSection,
    isSaving: isLoading,
    startEditing: (sectionKey) => setEditingSection(sectionKey),
    stopEditing: () => setEditingSection(null),
  }

  const content = (
    <RosterPlayerContext.Provider
      value={{
        isLoading,
        playerName: formatPlayerName(editSource),
        isSeeding,
        onCancel: handleClose,
        sportId,
        hasAdminPermission,
        requireEmail,
        isEdit,
        isAddExisting,
        emailLocked,
        namesLocked,
        salesforceIdLocked,
        showFamilySection,
        teamId,
        rosterId,
        familyPlayer: editSource,
      }}
    >
      {/* Supply an EditPlayerContext value so the reused profile sections
          (PersonalSection reads useEditPlayerContext) work. The target id is
          passed through so add-existing/edit show the player ID chip; create
          has none. */}
      <EditPlayerContext.Provider
        value={{ isLoading, onCancel: handleClose, playerId: targetPlayerId }}
      >
        {children}
      </EditPlayerContext.Provider>
    </RosterPlayerContext.Provider>
  )

  return (
    <Formik<RosterPlayerFormWithExtras>
      key={formikKey}
      // Edit: re-seed after a section save refetches the player. Deep-equal
      // initial values (a refetch of unchanged data) leave edits alone.
      enableReinitialize={isEdit}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {/* Read mode is the edit flow only; Add New / Add Existing stay plain,
          always-editable forms. */}
      {isEdit ? (
        <SectionEditContext.Provider value={sectionEdit}>
          {content}
        </SectionEditContext.Provider>
      ) : (
        content
      )}
    </Formik>
  )
}
